/**
 * Gör en uppladdad video webbvänlig: skalar ned till max 720p och kodar om
 * till mp4/webm som alla webbläsare kan spela. Filmer som redan är små och
 * i rätt format lämnas orörda.
 */

const MAX_HEIGHT = 720;
const SKIP_IF_SMALLER_THAN = 20 * 1024 * 1024; // 20 MB

export type PreparedVideo = {
  blob: Blob;
  mimeType: string;
  /** Filändelse utan punkt, t.ex. "mp4" eller "webm". */
  ext: string;
  /** Snyggt visningsnamn, utan råa kamerafilnamn. */
  label: string;
  /** True om vi kodade om filen. */
  converted: boolean;
};

function pickMimeType(): string | null {
  if (typeof MediaRecorder === "undefined") return null;
  const candidates = [
    "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
    "video/mp4",
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
  ];
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) ?? null;
}

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} kB`;
}

function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return "";
  const s = Math.round(seconds);
  return s >= 60 ? `${Math.floor(s / 60)} min ${s % 60} s` : `${s} s`;
}

export function videoLabel(sizeBytes: number, durationSeconds?: number): string {
  const parts = ["Din video"];
  const dur = formatDuration(durationSeconds ?? 0);
  if (dur) parts.push(dur);
  parts.push(formatSize(sizeBytes));
  return parts.join(" · ");
}

/** Kodar om videon i webbläsaren. Returnerar null om det inte går. */
async function transcode(
  file: File,
  mimeType: string,
  onProgress?: (percent: number) => void,
): Promise<{ blob: Blob; duration: number } | null> {
  const url = URL.createObjectURL(file);
  const el = document.createElement("video");
  el.src = url;
  el.muted = true;
  el.volume = 0;
  el.playsInline = true;
  el.preload = "auto";

  // Ljudet fångas via en tyst ljudväg – det kopplas aldrig till högtalarna.
  let audioContext: AudioContext | null = null;
  let audioSource: MediaElementAudioSourceNode | null = null;
  let audioDest: MediaStreamAudioDestinationNode | null = null;

  try {
    const ready = await new Promise<boolean>((resolve) => {
      const timer = window.setTimeout(() => resolve(false), 20000);
      el.onloadedmetadata = () => {
        window.clearTimeout(timer);
        resolve(true);
      };
      el.onerror = () => {
        window.clearTimeout(timer);
        resolve(false);
      };
    });
    if (!ready || !el.videoWidth || !el.videoHeight) return null;

    const duration = el.duration;
    const scale = Math.min(1, MAX_HEIGHT / el.videoHeight);
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(2, Math.round((el.videoWidth * scale) / 2) * 2);
    canvas.height = Math.max(2, Math.round((el.videoHeight * scale) / 2) * 2);
    const ctx = canvas.getContext("2d");
    if (!ctx || typeof canvas.captureStream !== "function") return null;

    const stream = canvas.captureStream(30);
    // Ta med ljudet, men bara in i inspelningen – aldrig ut i högtalarna.
    try {
      const Ctor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (Ctor) {
        audioContext = new Ctor();
        audioSource = audioContext.createMediaElementSource(el);
        audioDest = audioContext.createMediaStreamDestination();
        audioSource.connect(audioDest);
        audioDest.stream.getAudioTracks().forEach((track) => stream.addTrack(track));
        if (audioContext.state === "suspended") await audioContext.resume();
        // Ljudet går nu enbart genom ljudgrafen (aldrig till högtalarna),
        // så elementet får ljudnivå igen för att inspelningen ska få ljud.
        el.muted = false;
        el.volume = 1;
      }
    } catch {
      /* utan tyst ljudväg spelar vi in filmen utan ljud */
    }

    const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 2_500_000 });
    const chunks: BlobPart[] = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size) chunks.push(e.data);
    };

    const done = new Promise<Blob>((resolve) => {
      recorder.onstop = () => resolve(new Blob(chunks, { type: mimeType }));
    });

    let raf = 0;
    const draw = () => {
      ctx.drawImage(el, 0, 0, canvas.width, canvas.height);
      if (Number.isFinite(duration) && duration > 0) {
        onProgress?.(Math.min(99, Math.round((el.currentTime / duration) * 100)));
      }
      raf = requestAnimationFrame(draw);
    };

    recorder.start(1000);
    await el.play().catch(() => el.play());
    draw();

    await new Promise<void>((resolve) => {
      el.onended = () => resolve();
      // Säkerhetsnät om videon fastnar.
      window.setTimeout(resolve, ((Number.isFinite(duration) ? duration : 60) + 15) * 1000);
    });

    cancelAnimationFrame(raf);
    if (recorder.state !== "inactive") recorder.stop();
    const blob = await done;
    onProgress?.(100);
    if (!blob.size) return null;
    return { blob, duration };
  } catch {
    return null;
  } finally {
    el.pause();
    try {
      audioSource?.disconnect();
      audioDest?.disconnect();
      audioDest?.stream.getTracks().forEach((track) => track.stop());
      void audioContext?.close();
    } catch {
      /* uppstädning är best effort */
    }
    el.removeAttribute("src");
    el.load();
    URL.revokeObjectURL(url);
  }
}

async function readDuration(file: File): Promise<number> {
  const url = URL.createObjectURL(file);
  try {
    return await new Promise<number>((resolve) => {
      const el = document.createElement("video");
      el.preload = "metadata";
      el.onloadedmetadata = () => resolve(el.duration);
      el.onerror = () => resolve(0);
      el.src = url;
      window.setTimeout(() => resolve(0), 8000);
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function prepareVideoForWeb(
  file: File,
  onProgress?: (percent: number) => void,
): Promise<PreparedVideo> {
  const isWebFriendly = /video\/(mp4|webm)$/i.test(file.type) && !/\.mov$/i.test(file.name);
  const needsWork = !isWebFriendly || file.size > SKIP_IF_SMALLER_THAN;
  const mimeType = pickMimeType();

  if (needsWork && mimeType) {
    const out = await transcode(file, mimeType, onProgress);
    if (out && (out.blob.size < file.size || !isWebFriendly)) {
      const ext = mimeType.startsWith("video/mp4") ? "mp4" : "webm";
      return {
        blob: out.blob,
        mimeType: mimeType.split(";")[0],
        ext,
        label: videoLabel(out.blob.size, out.duration),
        converted: true,
      };
    }
  }

  const duration = await readDuration(file);
  const ext = file.type.includes("webm") ? "webm" : "mp4";
  return {
    blob: file,
    mimeType: file.type || "video/mp4",
    ext,
    label: videoLabel(file.size, duration),
    converted: false,
  };
}

/* ------------------------------------------------------------------ */
/* Kraftigare komprimering för videoskanningen                         */
/* ------------------------------------------------------------------ */

/** En del av filmen, färdig att laddas upp och skannas. */
export type ScanSegment = {
  file: File;
  /** Var i originalfilmen delen börjar respektive slutar (sekunder). */
  startSeconds: number;
  endSeconds: number;
  width: number;
  height: number;
  bytes: number;
  /** Hur lång tid just den här delen tog att koda om (ms). */
  elapsedMs: number;
};

export type ScanCompressResult = {
  segments: ScanSegment[];
  width: number;
  height: number;
  /** Filmens längd i sekunder. */
  durationSeconds: number;
  /** Total tid för hela komprimeringen (ms). */
  elapsedMs: number;
  mimeType: string;
};

/** Tillräckligt skarpt för att AI:n ska kunna läsa märken och skick. */
const SCAN_LONG_EDGE = 1080;
const SCAN_FPS = 30;
/** Varje del hålls under det här taket – annars startar vi en ny del. */
const SCAN_SEGMENT_MAX_BYTES = 18 * 1024 * 1024;
/** Hög bitrate: skärpan är viktigare än filstorleken nu när vi kan dela upp. */
const SCAN_BITRATE = 11_000_000;
/** Fler delar än så här blir för dyrt och för långsamt. */
export const SCAN_MAX_SEGMENTS = 6;

export function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / 1024 ** 3).toFixed(1).replace(".", ",")} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 ** 2).toFixed(1).replace(".", ",")} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} kB`;
}

export function canCompressVideo(): boolean {
  if (typeof window === "undefined") return false;
  const canvas = document.createElement("canvas");
  return (
    typeof MediaRecorder !== "undefined" &&
    typeof canvas.captureStream === "function" &&
    !!pickMimeType()
  );
}

/**
 * Kodar om filmen i hög kvalitet (1080p, 30 fps, hög bitrate) i normal hastighet
 * och delar upp den i flera filer så snart en del närmar sig storleksgränsen.
 * Varje del vet var i originalfilmen den börjar, så tidsstämplarna stämmer.
 */
export async function compressVideoForScan(
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<ScanCompressResult> {
  const mimeType = pickMimeType();
  if (!mimeType) throw new Error("Din telefon klarar inte att göra filmen redo. Prova en annan enhet.");
  const startedAt = performance.now();

  const url = URL.createObjectURL(file);
  const video = document.createElement("video");
  video.src = url;
  video.muted = true;
  video.playsInline = true;
  video.preload = "auto";

  try {
    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => resolve();
      video.onerror = () => reject(new Error("Filmen kunde inte läsas."));
    });

    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    const scale = Math.min(1, SCAN_LONG_EDGE / Math.max(video.videoWidth, video.videoHeight, 1));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(2, Math.round((video.videoWidth * scale) / 2) * 2);
    canvas.height = Math.max(2, Math.round((video.videoHeight * scale) / 2) * 2);
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Kunde inte förbereda filmen.");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    const type = mimeType.split(";")[0] || "video/mp4";
    const ext = type.includes("mp4") ? "mp4" : "webm";
    const baseName = (file.name.replace(/\.[^.]+$/, "") || "film").slice(0, 40);
    const stream = canvas.captureStream(SCAN_FPS);

    const segments: ScanSegment[] = [];
    let partIndex = 0;
    let done = false;

    while (!done && partIndex < SCAN_MAX_SEGMENTS) {
      const segStartedAt = performance.now();
      const startSeconds = video.currentTime;
      const parts: BlobPart[] = [];
      let bytes = 0;

      const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: SCAN_BITRATE });
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          parts.push(e.data);
          bytes += e.data.size;
        }
      };
      const stopped = new Promise<void>((resolve) => {
        recorder.onstop = () => resolve();
      });

      recorder.start(500);
      try {
        await video.play();
      } catch (error) {
        const wrapped = new Error(error instanceof Error ? error.message : "Webbläsaren stoppade videokodningen.", { cause: error });
        wrapped.name = error instanceof Error ? error.name : "VideoPlaybackError";
        (wrapped as Error & { code?: string }).code = "TRANSCODE_PLAY_BLOCKED";
        throw wrapped;
      }

      let raf = 0;
      const draw = () => {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        if (duration > 0) onProgress?.(Math.min(0.99, video.currentTime / duration));
        raf = requestAnimationFrame(draw);
      };
      draw();

      // Vi kör tills filmen tar slut, delen blir för stor, eller vi tar för lång tid.
      await new Promise<void>((resolve) => {
        let settled = false;
        const finish = (ended: boolean) => {
          if (settled) return;
          settled = true;
          done = ended;
          window.clearInterval(sizeTimer);
          resolve();
        };
        const sizeTimer = window.setInterval(() => {
          if (bytes >= SCAN_SEGMENT_MAX_BYTES) finish(false);
        }, 250);
        video.onended = () => finish(true);
        window.setTimeout(() => finish(true), Math.max(8000, duration * 1000 + 10000));
      });

      video.pause();
      cancelAnimationFrame(raf);
      if (recorder.state !== "inactive") recorder.stop();
      await stopped;

      const endSeconds = done ? duration || video.currentTime : video.currentTime;
      const blob = new Blob(parts, { type });
      if (blob.size > 0) {
        segments.push({
          file: new File([blob], `${baseName}-del${partIndex + 1}.${ext}`, { type }),
          startSeconds,
          endSeconds,
          width: canvas.width,
          height: canvas.height,
          bytes: blob.size,
          elapsedMs: Math.round(performance.now() - segStartedAt),
        });
      }
      partIndex += 1;

      if (!done) {
        // Backa en halv sekund så inget föremål hamnar mitt i skarven.
        video.currentTime = Math.max(0, video.currentTime - 0.5);
        await new Promise<void>((resolve) => {
          video.onseeked = () => resolve();
          window.setTimeout(resolve, 1500);
        });
      }
    }

    onProgress?.(1);
    if (!segments.length) throw new Error("Filmen gick inte att göra redo. Prova en kortare film.");

    return {
      segments,
      width: canvas.width,
      height: canvas.height,
      durationSeconds: duration,
      elapsedMs: Math.round(performance.now() - startedAt),
      mimeType: type,
    };
  } finally {
    video.pause();
    video.removeAttribute("src");
    video.load();
    URL.revokeObjectURL(url);
  }
}


